import React from 'react';
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Dialog } from '../../others/Dialog';
import { ActionButtons } from '../../others/ActionButtons';
import { useTranslation } from 'react-i18next';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP },
    },
};

/**
 * DialogEmailRecipients
 *
 * All state and logic live in useEmailRecipients (injected via props).
 * This component is purely presentational.
 *
 * Props (all come from the hook):
 *  - open                {boolean}
 *  - onClose             {fn}
 *  - onConfirm           {fn}          called when the user clicks Confirm
 *  - catalogue           {string[]}    full list of known emails
 *  - selected            {string[]}    currently selected emails
 *  - newEmail            {string}
 *  - newEmailError       {string}
 *  - onNewEmailChange    {fn(value)}
 *  - onAddEmail          {fn}
 *  - onSelectChange      {fn(event)}
 *  - onRemoveChip        {fn(email)}
 */
export const DialogEmailRecipients = ({
    open,
    onClose,
    onConfirm,
    catalogue = [],
    selected = [],
    newEmail = '',
    newEmailError = '',
    onNewEmailChange,
    onAddEmail,
    onSelectChange,
    onRemoveChip,
}) => {
    const { t } = useTranslation();

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onAddEmail();
        }
    };

    return (
        <Dialog
            openDialog={open}
            widthDialog="sm"
            styleDialog={{ width: '480px' }}
            title={
                <Typography sx={{ fontWeight: 'bold', fontSize: 17 }}>
                    {t('invoice.emailRecipientsTitle', 'Destinatarios del correo')}
                </Typography>
            }
            styleTitle={{ px: 3, pt: 3 }}
            content={
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                    {/* ── new email input ── */}
                    <TextField
                        fullWidth
                        size="small"
                        label={t('invoice.addNewEmail', 'Agregar nuevo correo')}
                        value={newEmail}
                        onChange={(e) => onNewEmailChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        error={Boolean(newEmailError)}
                        helperText={
                            newEmailError ||
                            t('invoice.addEmailHint', 'Presiona Enter o el botón (+) para agregar')
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={onAddEmail}
                                        color="primary"
                                        disabled={!newEmail.trim()}
                                        title={t('add', 'Agregar')}
                                    >
                                        <AddCircleOutlineIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* ── multi-select ── */}
                    {catalogue.length > 0 ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="email-recipients-label">
                                {t('invoice.selectRecipients', 'Seleccionar destinatarios')}
                            </InputLabel>
                            <Select
                                labelId="email-recipients-label"
                                multiple
                                value={selected}
                                onChange={onSelectChange}
                                label={t('invoice.selectRecipients', 'Seleccionar destinatarios')}
                                renderValue={(vals) => vals.join(', ')}
                                MenuProps={MenuProps}
                                style={{ width: '100%', minHeight: 40 }}
                            >
                                {catalogue.map((email) => (
                                    <MenuItem key={email} value={email}>
                                        <Checkbox checked={selected.includes(email)} size="small" />
                                        <ListItemText primary={email} />
                                    </MenuItem>
                                ))}
                            </Select>
                            {selected.length === 0 && (
                                <FormHelperText>
                                    {t('invoice.atLeastOneEmail', 'Selecciona al menos un destinatario')}
                                </FormHelperText>
                            )}
                        </FormControl>
                    ) : (
                        <Alert severity="info" sx={{ fontSize: 13 }}>
                            {t('invoice.noEmailsAvailable', 'No hay correos registrados. Agrega uno arriba.')}
                        </Alert>
                    )}

                    {/* ── selected chips ── */}
                    {selected.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                            {selected.map((email) => (
                                <Chip
                                    key={email}
                                    label={email}
                                    size="small"
                                    onDelete={() => onRemoveChip(email)}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            }
            styleContent={{ px: 3, pb: 1 }}
            actions={
                <Grid size={{ md: 12 }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 1
                    }}>
                    <ActionButtons
                        onCancel={onClose}
                        onSubmit={onConfirm}
                        disabledSubmit={selected.length === 0}
                    />
                    
                </Grid>
            }
        />
    );
};
